// How a tabular result's cells are written. Pure, so the rules can be pinned by
// tests and shared by the card and the wizard preview.
//
// The agent is told to write plain values (no separators, units or currency; dates
// as YYYY-MM-DD; '__NULL__' for a missing cell) and resapi types each column as
// number, date or text. Formatting adds readability and caps a number column at two
// decimals, the same display rule chat applies to computed decimals, so a value here
// still reads the same as the value the same question gets in chat.

import { KPI_PLACEHOLDER } from './kpiFormat';
import { roundDecimalString } from '../../../../utils/numberFormat';

export const NULL_CELL = '__NULL__';
export const EMPTY_MARK = KPI_PLACEHOLDER;

export function isNullCell(raw) {
  return raw == null || raw === NULL_CELL || String(raw).trim() === '';
}

// Separators from the locale; at most two decimals, the console-wide display rule.
// A value with two places or fewer keeps exactly the ones it carries ("1.50" stays
// "1.50"); one with more is rounded to two and loses its trailing zeros. "1234.5" →
// "1,234.5", "3.14159" → "3.14", "96.0000" → "96", "abc" → "abc" (a number column
// can still hold a stray label; showing it beats showing NaN). The CSV download does
// not come through here, so it keeps every digit.
//
// The rounding is done on the written digits (roundDecimalString), and only then is
// the result checked for a lossless round trip through a JS number: re-serialise the
// parsed number to the same number of decimals, and add separators only if it comes
// back identical. An integer past 2^53 — an ID, an external record number — returns
// different digits, and a table that quietly shows 9,007,199,254,740,992 for
// 9007199254740993 is worse than one that shows no separators at all. The same test
// keeps "-0" from becoming "0".
//
// Deliberately conservative at the edges: a value whose written form differs only
// cosmetically, such as a leading zero, also fails the round trip and is shown as
// written (rounded, if it had more than two places). Showing the source exactly is
// never wrong; changing its digits is.
export function formatNumberCell(raw, locale) {
  const written = String(raw).trim();
  const s = roundDecimalString(written) ?? written;
  const n = Number(s);
  if (s === '' || !Number.isFinite(n)) return s;
  const dot = s.indexOf('.');
  const decimals = dot === -1 ? 0 : Math.min(20, s.length - dot - 1);
  if (n.toFixed(decimals) !== s) return s;
  return new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n);
}

// YYYY-MM-DD is a calendar date, not an instant: parse it as UTC and format in UTC
// so a viewer west of Greenwich does not see the previous day. A trailing time
// (YYYY-MM-DDTHH:MM[:SS], with an optional Z or ±HH:MM) is kept and shown.
//
// An explicit offset is HONOURED, not dropped. A value carrying one names an
// instant rather than a wall-clock reading, so reading its digits as UTC would
// relabel it: 13:45+05:30 and 13:45Z are different moments, and rendering both as
// 1:45 PM makes two different instants look identical while making one instant
// written two ways look different. That would undo the very thing UTC parsing is
// here for. This matters more now that rows come from the tool payload rather than
// from a model instructed to write plain dates, so raw timestamps reach this.
//
// Anchored at BOTH ends on purpose. Unanchored, "2026-12-07 to 2026-12-14" matched
// its first date and rendered as "Dec 7, 2026" — the range silently lost its end.
// Anything this does not match in full is shown as written.
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::\d{2})?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})?)?$/;
export function formatDateCell(raw, locale) {
  const s = String(raw).trim();
  const m = DATE_RE.exec(s);
  if (!m) return s;
  const [, y, mo, d, hh, mm, offset] = m;
  // The calendar date is validated on the digits as written, so an impossible day
  // like 2026-02-30 is rejected before any offset is applied and rolls it into a
  // real one.
  const wall = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(hh || 0), Number(mm || 0)));
  if (Number.isNaN(wall.getTime()) || wall.getUTCMonth() !== Number(mo) - 1) return s;
  const date = offset ? new Date(s.replace(' ', 'T')) : wall;
  if (Number.isNaN(date.getTime())) return s;
  const opts = { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' };
  if (hh != null) Object.assign(opts, { hour: 'numeric', minute: '2-digit' });
  return new Intl.DateTimeFormat(locale, opts).format(date);
}

// A header the agent wrote as a column name (snake_case) reads as words; a header
// that is already words, a code (HAM1) or an acronym is left alone. The raw name
// stays on the header's tooltip either way.
export function humanizeHeader(name) {
  const s = String(name ?? '');
  if (!s.includes('_')) return s;
  return s.split('_').filter(Boolean).map((w) => (w === w.toUpperCase() ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
}

export function normalizeColumnType(type) {
  const t = String(type || '').trim().toLowerCase();
  return t === 'number' || t === 'date' ? t : 'text';
}

// One cell → what the tile shows and how it is aligned.
//
// PRD-1578: an identifier column (resapi's `identifier_columns`, one flag per column)
// is still a number -- it right-aligns and sorts like one -- but its digits are shown
// as written. "104,233" reads as a quantity; an employee number or a year is not one.
export function formatCell(raw, columnType, locale, { identifier = false } = {}) {
  if (isNullCell(raw)) return { text: EMPTY_MARK, isNull: true, type: normalizeColumnType(columnType) };
  const type = normalizeColumnType(columnType);
  // A NUMERIC id arrives as "104233.00"; its zero fraction is storage, not part of it.
  if (type === 'number' && identifier) return { text: String(raw).trim().replace(/^(-?\d+)\.0+$/, '$1'), isNull: false, type };
  if (type === 'number') return { text: formatNumberCell(raw, locale), isNull: false, type };
  if (type === 'date') return { text: formatDateCell(raw, locale), isNull: false, type };
  return { text: String(raw), isNull: false, type };
}

// The caption under the table. M is the backend's total, never counted here; the
// live metadata (absent for a stored result) can say the total itself is uncertain.
export function rowCountCaption(shownCount, totalRows, meta) {
  const total = Number(totalRows) || 0;
  if (meta && meta.total_rows_known === false) {
    return `Showing the first ${shownCount.toLocaleString()} rows; more may match`;
  }
  if (total > shownCount) return `Showing ${shownCount.toLocaleString()} of ${total.toLocaleString()} rows`;
  return `${shownCount.toLocaleString()} ${shownCount === 1 ? 'row' : 'rows'}`;
}
